import { useState } from 'react';
import type { DocumentBlockConfig } from '@mem-ai-hub/shared';
import { generateDocumentBlock } from '@/services/documentApi';

export function tryParseDocument(text: string): DocumentBlockConfig | null {
  // Trim whitespace/newlines that markdown parsers may add around the JSON
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return null;

  try {
    const obj = JSON.parse(trimmed);
    if (
      obj &&
      typeof obj === 'object' &&
      obj.type === 'document' &&
      typeof obj.title === 'string' &&
      typeof obj.fileName === 'string' &&
      Array.isArray(obj.sections) &&
      obj.sections.length > 0
    ) {
      return obj as DocumentBlockConfig;
    }
  } catch {
    // If the text looks like complete JSON (has closing brace) but failed to parse,
    // try extracting just the JSON object in case there are trailing characters
    if (trimmed.endsWith('}')) {
      try {
        // Find the outermost JSON object
        const lastBrace = trimmed.lastIndexOf('}');
        const obj = JSON.parse(trimmed.slice(0, lastBrace + 1));
        if (
          obj &&
          typeof obj === 'object' &&
          obj.type === 'document' &&
          typeof obj.title === 'string' &&
          typeof obj.fileName === 'string' &&
          Array.isArray(obj.sections) &&
          obj.sections.length > 0
        ) {
          return obj as DocumentBlockConfig;
        }
      } catch {
        // genuinely invalid JSON
      }
    }
  }
  return null;
}

interface DocumentBlockProps {
  config: DocumentBlockConfig;
}

export default function DocumentBlock({ config }: DocumentBlockProps) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sectionCount = config.sections.length;
  const headingCount = config.sections.filter((s) => s.heading).length;

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const result = await generateDocumentBlock(config);
      window.open(result.downloadUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="my-3 rounded-lg border border-border-light bg-bg-secondary p-3">
      <div className="flex flex-wrap items-center gap-3">
        {/* Document icon */}
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#03A9F4]/10">
          <svg className="h-5 w-5 text-[#03A9F4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        </div>

        {/* Title and metadata */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text-primary font-heading">
            {config.title}
          </p>
          <p className="text-xs text-text-secondary">
            {headingCount} {headingCount === 1 ? 'section' : 'sections'}, {sectionCount} {sectionCount === 1 ? 'block' : 'blocks'}
          </p>
        </div>

        {/* Download button */}
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#03A9F4] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#0288D1] disabled:opacity-50"
        >
          {downloading ? (
            <>
              <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download .docx
            </>
          )}
        </button>
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
